import React, { useState } from "react";
import { FloatingPortal, autoUpdate, flip, offset, shift, safePolygon, useClick,
  useDismiss, useFloating, useFocus, useHover, useInteractions, useRole } from "@floating-ui/react";
import { FileText, Link2, Image, User, BookOpen } from "lucide-react";

const ICONS = { file: FileText, image: Image, url: Link2, person: User };

// Popups are portalled outside the original-text render, preserving its hash.
// Floating UI handles wrapped spans, clipping ancestors and viewport edges.
export function LinkPreview({ node, memory, testId, className, onSelect, strategy }) {
  const [open, setOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({ open, onOpenChange: setOpen,
    placement: "bottom-start", whileElementsMounted: autoUpdate,
    middleware: [offset(9), flip({ padding: 12 }), shift({ padding: 12 })] });
  const hover = useHover(context, { delay: { open: 100, close: 100 }, handleClose: safePolygon(), mouseOnly: true });
  const focus = useFocus(context);
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, click, dismiss, role]);
  const Icon = ICONS[memory?.type] || BookOpen;
  const isDeixis = node.type === "deixis";
  const scores = node.matcher_scores || {};
  const evidence = isDeixis ? (node.binding_source === "working_context" ? "Working context binding" : "Recent context")
    : scores.exact > 0 ? "Exact name or alias" : scores.phonetic > 0 ? "Similar pronunciation"
    : scores.fuzzy > 0 ? "Similar spelling" : "Conceptual match";
  const score = node.calibrated_confidence ?? node.confidence;

  return <>
    <span ref={refs.setReference} data-testid={testId} tabIndex={0} role="button"
      className={className} {...getReferenceProps({ onClick: () => onSelect?.(node) })}>
      {node.text}
    </span>
    {open && <FloatingPortal>
      <div ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}
        className="memory-preview" data-testid={`${testId}-preview`}>
        <div className="flex items-start gap-2.5">
          <Icon size={17} className="text-teal shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-[10px] text-muted uppercase font-mono" data-testid={`${testId}-preview-type`}>
              {memory?.type || "Unresolved reference"}{node.ambiguous ? " · ambiguous" : ""}
            </div>
            <div className="font-semibold text-fg text-sm" data-testid={`${testId}-preview-name`}>
              {memory?.canonical || "Needs a context binding"}
            </div>
          </div>
        </div>
        <p className="mt-2 text-[13px] leading-relaxed text-fg/85" data-testid={`${testId}-preview-description`}>
          {memory?.description || (memory ? "No description saved for this memory." : "No confident memory match for this reference.")}
        </p>
        {memory?.pointer && <div className="mt-2 font-mono text-[11px] text-teal" data-testid={`${testId}-preview-pointer`}>{memory.pointer}</div>}
        {memory && <div className="mt-3 pt-2 border-t border-line flex flex-wrap justify-between gap-1 text-[10px] font-mono text-muted" data-testid={`${testId}-preview-evidence`}>
          <span>{evidence}</span><span>{typeof score === "number" ? `${Math.round(score * 100)}% confidence` : node.binding_source}</span>
          {!isDeixis && <span className="w-full">{strategy}</span>}
        </div>}
      </div>
    </FloatingPortal>}
  </>;
}